export const DURATION_FRAMES = 193;

import React from "react";
import { AbsoluteFill, Audio, Sequence, staticFile, useCurrentFrame, interpolate, spring, useVideoConfig } from "remotion";
import { noise2D } from "@remotion/noise";
import { dark, fonts, safeZone, gradients } from "../styles/theme";

/*
 * Reel_cmohom8to00003qo6reqa1z32
 * [TEST] Reel de prueba
 * 193 frames @ 30fps
 * Scene timings:
 *   Scene 1 "Test": from=0, duration=193
 */

const TOTAL_FRAMES = 193;

const FloatingParticles: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      {Array.from({ length: 16 }).map((_, i) => {
        const nx = noise2D("particle-x-" + i, frame * 0.008, 0);
        const ny = noise2D("particle-y-" + i, frame * 0.008, 0);
        const x = (nx * 0.5 + 0.5) * 1080;
        const y = (ny * 0.5 + 0.5) * 1920;
        const hue = 200 + noise2D("particle-h-" + i, frame * 0.004, 0) * 30;
        const size = 3 + (i % 4);
        const opacity = 0.06 + ((i % 3) * 0.015);
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: x,
              top: y,
              width: size,
              height: size,
              borderRadius: "50%",
              background: `hsl(${hue}, 80%, 60%)`,
              opacity,
              boxShadow: `0 0 ${size * 4}px hsl(${hue}, 80%, 60%)`,
            }}
          />
        );
      })}
    </AbsoluteFill>
  );
};

const LightSweep: React.FC = () => {
  const frame = useCurrentFrame();
  const left = interpolate(frame, [0, TOTAL_FRAMES], [-300, 1280], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={{ pointerEvents: "none", overflow: "hidden" }}>
      <div
        style={{
          position: "absolute",
          left,
          top: 0,
          width: 220,
          height: "100%",
          background:
            "linear-gradient(90deg, transparent, rgba(96,165,250,0.06), rgba(255,255,255,0.04), transparent)",
          transform: "skewX(-12deg)",
          filter: "blur(8px)",
        }}
      />
    </AbsoluteFill>
  );
};

const VignetteOverlay: React.FC = () => {
  const frame = useCurrentFrame();
  const opacity = interpolate(frame, [0, 60, TOTAL_FRAMES], [0.55, 0.45, 0.5], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill
      style={{
        pointerEvents: "none",
        background: `radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,${opacity}) 100%)`,
      }}
    />
  );
};

const GlobalGlow: React.FC = () => {
  const frame = useCurrentFrame();
  const blueOpacity = interpolate(
    frame,
    [0, 30, 120, TOTAL_FRAMES],
    [0, 0.9, 1, 0.8],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: gradients.darkGlow,
          opacity: blueOpacity,
        }}
      />
    </AbsoluteFill>
  );
};

const RisingBokeh: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      {Array.from({ length: 10 }).map((_, i) => {
        const seed = i * 137;
        const baseX = ((seed * 13) % 1000) + 40;
        const phase = (frame + i * 18) % 180;
        const y = interpolate(phase, [0, 180], [1900, -100], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });
        const opacity = interpolate(phase, [0, 30, 150, 180], [0, 0.35, 0.35, 0], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });
        const size = 6 + (i % 4) * 3;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: baseX,
              top: y,
              width: size,
              height: size,
              borderRadius: "50%",
              background: "rgba(96,165,250,0.6)",
              boxShadow: "0 0 20px rgba(96,165,250,0.8)",
              opacity,
            }}
          />
        );
      })}
    </AbsoluteFill>
  );
};

const ContinuousBackground: React.FC = () => {
  return (
    <AbsoluteFill>
      <GlobalGlow />
      <FloatingParticles />
      <RisingBokeh />
      <LightSweep />
      <VignetteOverlay />
    </AbsoluteFill>
  );
};

const CoreDigitalLogo: React.FC<{ opacity: number }> = ({ opacity }) => {
  return (
    <div
      style={{
        position: "absolute",
        bottom: safeZone.bottom + 40,
        left: 0,
        right: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 14,
        opacity,
      }}
    >
      <svg width="42" height="42" viewBox="0 0 42 42">
        <defs>
          <linearGradient id="logoGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#60a5fa" />
            <stop offset="100%" stopColor="#2563eb" />
          </linearGradient>
        </defs>
        <circle cx="21" cy="21" r="18" fill="none" stroke="url(#logoGrad)" strokeWidth="2.5" />
        <circle cx="21" cy="21" r="8" fill="url(#logoGrad)" />
      </svg>
      <div
        style={{
          fontFamily: fonts.headline,
          fontSize: 26,
          fontWeight: 700,
          color: dark.text,
          letterSpacing: 1,
        }}
      >
        Core <span style={{ color: dark.accentBlueMuted }}>Digital</span>
      </div>
    </div>
  );
};

const Scene1: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const cardEnter = spring({
    frame,
    fps,
    config: { damping: 20, stiffness: 80, mass: 1 },
  });

  const cardOpacity = interpolate(frame, [0, 25], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const cardTranslate = interpolate(cardEnter, [0, 1], [60, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const labelOpacity = interpolate(frame, [10, 35], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const labelTranslate = interpolate(frame, [10, 35], [30, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const titleOpacity = interpolate(frame, [22, 50], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const titleTranslate = interpolate(frame, [22, 50], [60, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const sublineOpacity = interpolate(frame, [44, 72], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const sublineTranslate = interpolate(frame, [44, 72], [30, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const progress = interpolate(frame, [60, TOTAL_FRAMES - 10], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const logoOpacity = interpolate(frame, [80, 110], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const pulseScale = 1 + Math.sin(frame * 0.12) * 0.015;

  return (
    <AbsoluteFill
      style={{
        paddingTop: safeZone.top,
        paddingBottom: safeZone.bottom,
        paddingLeft: safeZone.horizontal,
        paddingRight: safeZone.horizontal,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        style={{
          opacity: cardOpacity,
          transform: `translateY(${cardTranslate}px) scale(${pulseScale})`,
          background: dark.surface,
          borderRadius: 28,
          border: `1px solid ${dark.border}`,
          boxShadow: dark.glowBlueStrong,
          padding: "56px 48px",
          width: "100%",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 28,
        }}
      >
        <div
          style={{
            opacity: labelOpacity,
            transform: `translateY(${labelTranslate}px)`,
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "10px 22px",
            borderRadius: 999,
            background: "rgba(37,99,235,0.12)",
            border: "1px solid rgba(96,165,250,0.3)",
          }}
        >
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: dark.accentBlueMuted,
              boxShadow: `0 0 12px ${dark.accentBlueMuted}`,
            }}
          />
          <div
            style={{
              fontFamily: fonts.body,
              fontSize: 24,
              fontWeight: 600,
              color: dark.accentBlueMuted,
              letterSpacing: 3,
              textTransform: "uppercase",
            }}
          >
            Pipeline · Test
          </div>
        </div>

        <div
          style={{
            opacity: titleOpacity,
            transform: `translateY(${titleTranslate}px)`,
            fontFamily: fonts.headline,
            fontSize: 92,
            fontWeight: 800,
            lineHeight: 1.05,
            textAlign: "center",
            background: "linear-gradient(135deg, #60a5fa, #2563eb, #818cf8)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            letterSpacing: -1,
          }}
        >
          Esto es un test
        </div>

        <div
          style={{
            opacity: sublineOpacity,
            transform: `translateY(${sublineTranslate}px)`,
            fontFamily: fonts.body,
            fontSize: 32,
            fontWeight: 500,
            color: dark.textSecondary,
            textAlign: "center",
            maxWidth: 720,
            lineHeight: 1.4,
          }}
        >
          Verificando el pipeline completo de generación
        </div>

        <div
          style={{
            opacity: sublineOpacity,
            width: "70%",
            height: 6,
            borderRadius: 999,
            background: "rgba(255,255,255,0.06)",
            overflow: "hidden",
            marginTop: 12,
          }}
        >
          <div
            style={{
              width: `${progress * 100}%`,
              height: "100%",
              background: "linear-gradient(90deg, #60a5fa, #2563eb, #818cf8)",
              borderRadius: 999,
              boxShadow: "0 0 20px rgba(96,165,250,0.6)",
            }}
          />
        </div>
      </div>

      <CoreDigitalLogo opacity={logoOpacity} />
    </AbsoluteFill>
  );
};

const Reel_cmohom8to00003qo6reqa1z32: React.FC = () => {
  return (
    <AbsoluteFill style={{ background: dark.bg }}>
      <ContinuousBackground />
      <Sequence from={5}>
        <Audio src={staticFile("audio/cmohom8to00003qo6reqa1z32.mp3")} />
      </Sequence>
      <Sequence from={0} durationInFrames={193}>
        <Scene1 />
      </Sequence>
    </AbsoluteFill>
  );
};

export default Reel_cmohom8to00003qo6reqa1z32;
;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
